import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TableSortLabel,
} from '@mui/material';
import {  
  InfoOutlined,
  TrendingUp,
  Assessment,
  ExpandMore,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import OCIBadge from '../../components/oci/OCIBadge';
import OCIFactorsList from '../../components/oci/OCIFactorsList';
import { ociService } from '../../services/ociService';
import { usePagesList } from '../../hooks/usePagesList';
import { useSiteStore } from '../../store/site.store';

interface PageOCIData {
  pageId: number;
  url: string;
  score?: number;
  level?: string;
  loading: boolean;
  error?: string;
}

export default function OciListPage() {
  const selectedSite = useSiteStore(state => state.selectedSite);
  const [engine, setEngine] = useState<'google' | 'bing'>('google');
  const [window, setWindow] = useState<7 | 28 | 90>(28);
  const [ociRangeFilter, setOciRangeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [pagesOCIData, setPagesOCIData] = useState<PageOCIData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPageForDetail, setSelectedPageForDetail] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // Use the common prepopulated pages list
  const { pages, loading: pagesLoading } = usePagesList();

  // Load OCI data for all pages
  const loadAllOCIData = async () => {
    if (!pages || pages.length === 0) return;
    
    setLoading(true);
    const initialData: PageOCIData[] = pages.map(p => ({
      pageId: p.id,
      url: p.url,
      loading: true,
    }));
    setPagesOCIData(initialData);

    // Fetch OCI for each page
    const results = await Promise.allSettled(
      pages.map(page => 
        ociService.getOCIScore(page.id, engine, window)
          .catch(err => ({ error: err.message, pageId: page.id }))
      )
    );

    const updatedData: PageOCIData[] = pages.map((page, index) => {
      const result = results[index];
      if (result.status === 'fulfilled') {
        const data = result.value as any;
        if ('error' in data) {
          return {
            pageId: page.id,
            url: page.url,
            loading: false,
            error: data.error === 'OCI_COMPUTING' ? 'Computing...' : data.error,
          };
        }
        return {
          pageId: page.id,
          url: page.url,
          score: data.score,
          level: data.level,
          loading: false,
        };
      } else {
        return {
          pageId: page.id,
          url: page.url,
          loading: false,
          error: 'Failed to load',
        };
      }
    });

    setPagesOCIData(updatedData);
    setLoading(false);
  };

  // Fetch OCI score for detail dialog
  const ociDetailQuery = useQuery({
    queryKey: ['oci-detail', selectedPageForDetail, engine, window],
    queryFn: () => ociService.getOCIScore(selectedPageForDetail!, engine, window),
    enabled: !!selectedPageForDetail && dialogOpen,
  });

  // Recompute mutation
  const recomputeMutation = useMutation({
    mutationFn: () => ociService.recomputeOCI(selectedPageForDetail!, engine, window),
    onSuccess: () => {
      ociDetailQuery.refetch();
      loadAllOCIData(); // Refresh the list
    },
  });

  const ociData = ociDetailQuery.data;
  const insufficientData = ociData?.minImpressionsRequired && 
    ociData?.actualImpressions && 
    ociData.actualImpressions < ociData.minImpressionsRequired;

  // Filter and sort data
  const filteredData = pagesOCIData
    .filter(item => {
      // Search filter
      if (searchQuery && !item.url.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      // OCI range filter
      if (ociRangeFilter !== 'all' && item.score !== undefined) {
        if (ociRangeFilter === 'low' && (item.score < 0 || item.score > 33)) return false;
        if (ociRangeFilter === 'medium' && (item.score < 34 || item.score > 66)) return false;
        if (ociRangeFilter === 'high' && (item.score < 67 || item.score > 100)) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by score descending (highest competition first)
      if (a.score === undefined) return 1;
      if (b.score === undefined) return -1;
      return b.score - a.score;
    });

  const handleRowClick = (pageId: number) => {
    setSelectedPageForDetail(pageId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPageForDetail(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Info Panels - Collapsible */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <InfoOutlined color="info" sx={{ mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight="bold">How OCI Works</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" component="div" sx={{ fontSize: '0.875rem', mb: 2 }}>
            OCI combines <strong>4 first-party signals</strong> from your Google Search Console / Bing Webmaster data:
          </Typography>
          <Stack spacing={1}>
            <Box>
              <Chip label="1" size="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span" fontWeight="bold">Impression Volatility: </Typography>
              <Typography variant="body2" component="span" color="text.secondary">
                High daily fluctuation = competitive SERP with frequent ranking changes
              </Typography>
            </Box>
            <Box>
              <Chip label="2" size="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span" fontWeight="bold">Position Variance: </Typography>
              <Typography variant="body2" component="span" color="text.secondary">
                Unstable rankings indicate active competition for keywords
              </Typography>
            </Box>
            <Box>
              <Chip label="3" size="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span" fontWeight="bold">CTR Responsiveness: </Typography>
              <Typography variant="body2" component="span" color="text.secondary">
                How much CTR changes with position shifts reveals click competition intensity
              </Typography>
            </Box>
            <Box>
              <Chip label="4" size="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span" fontWeight="bold">Experiment Win Rate: </Typography>
              <Typography variant="body2" component="span" color="text.secondary">
                Historical A/B test success shows how hard it is to improve this page
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            <strong>Final Score:</strong> 0-100 (LOW/MEDIUM/HIGH/VERY_HIGH) showing competitiveness level
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <TrendingUp color="success" sx={{ mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight="bold">Business Value</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Use OCI to make data-driven investment decisions:
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="body2"><strong>LOW OCI (0-33):</strong></Typography>
              <Typography variant="body2" color="text.secondary">
                ✅ Good opportunity - Low competition detected. Small improvements likely to yield results. <strong>Recommended for quick wins.</strong>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2"><strong>MEDIUM OCI (34-66):</strong></Typography>
              <Typography variant="body2" color="text.secondary">
                ⚠️ Moderate competition - Focused effort recommended. Need quality content + technical optimization. <strong>ROI depends on execution quality.</strong>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2"><strong>HIGH OCI (67-85):</strong></Typography>
              <Typography variant="body2" color="text.secondary">
                ⚠️ High competition - Substantial resources required. Expect longer timelines, need strong E-E-A-T, quality backlinks, and ongoing optimization. <strong>High effort, delayed ROI.</strong>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2"><strong>VERY HIGH OCI (86-100):</strong></Typography>
              <Typography variant="body2" color="text.secondary">
                ❌ Extremely competitive - Consider alternative strategies. May be better to target long-tail variations or create differentiated content angles. <strong>Very low success probability.</strong>
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Assessment color="action" sx={{ mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight="bold">Technical Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ fontSize: '0.875rem' }}>
            • <strong>Data Source:</strong> 100% first-party (Google Search Console + Bing Webmaster Tools)<br />
            • <strong>Analysis Window:</strong> 7, 28 or 90 days (configurable)<br />
            • <strong>Cache:</strong> Scores refresh every 24 hours (click "Recompute" to force update)<br />
            • <strong>Engine Support:</strong> Separate OCI for Google vs Bing (different competitive landscapes)<br />
            • <strong>Privacy:</strong> No third-party keyword databases or competitor scraping
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* OCI List Dashboard */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h5">
            Observed Competitiveness Index (OCI) - All Pages
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => {
              console.log('Opening OCI factors guide...');
              const newWindow = window.open('/oci-factors-guide.html', '_blank', 'width=1200,height=800,scrollbars=yes');
              if (!newWindow) {
                alert('Please allow pop-ups for this site to view the OCI Factors Guide');
              }
            }}
            sx={{ color: 'primary.main' }}
            title="View OCI Factors Comparison Guide"
          >
            <InfoOutlined />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {selectedSite?.name || 'Site'} - Investment Priority Analysis
        </Typography>

        {/* Filters and Controls */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Engine Selector */}
              <ToggleButtonGroup
                value={engine}
                exclusive
                onChange={(_, val) => val && setEngine(val)}
                size="small"
              >
                <ToggleButton value="google">GOOGLE</ToggleButton>
                <ToggleButton value="bing">BING</ToggleButton>
              </ToggleButtonGroup>

              {/* Window Selector */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Time Window</InputLabel>
                <Select
                  value={window}
                  label="Time Window"
                  onChange={(e) => setWindow(e.target.value as 7 | 28 | 90)}
                >
                  <MenuItem value={7}>7 days</MenuItem>
                  <MenuItem value={28}>28 days</MenuItem>
                  <MenuItem value={90}>90 days</MenuItem>
                </Select>
              </FormControl>

              {/* OCI Range Filter */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>OCI Range</InputLabel>
                <Select
                  value={ociRangeFilter}
                  label="OCI Range"
                  onChange={(e) => setOciRangeFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="low">Low (0-33)</MenuItem>
                  <MenuItem value="medium">Medium (34-66)</MenuItem>
                  <MenuItem value="high">High (67-100)</MenuItem>
                </Select>
              </FormControl>

              {/* Search */}
              <TextField
                size="small"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ minWidth: 250 }}
              />

              {/* Load Button */}
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={loadAllOCIData}
                disabled={loading || pagesLoading}
              >
                Load
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* Results */}
        {pagesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress sx={{ mr: 2 }} />
            <Typography>Loading pages...</Typography>
          </Box>
        ) : pagesOCIData.length === 0 ? (
          <Alert severity="info">
            Click "Load" button to fetch OCI scores for all pages in {selectedSite?.name || 'this site'}.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    <TableSortLabel active direction="desc">
                      OCI Score
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Level</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((item) => (
                  <TableRow
                    key={item.pageId}
                    hover
                    onClick={() => !item.loading && !item.error && handleRowClick(item.pageId)}
                    sx={{ 
                      cursor: item.loading || item.error ? 'default' : 'pointer',
                      '&:hover': {
                        bgcolor: item.loading || item.error ? 'transparent' : 'action.hover',
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                        {item.url}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {item.pageId}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {item.loading ? (
                        <CircularProgress size={20} />
                      ) : item.error ? (
                        <Typography variant="caption" color="error">
                          {item.error}
                        </Typography>
                      ) : item.score !== undefined ? (
                        <Typography variant="body2" fontWeight="600">
                          {item.score.toFixed(1)}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {!item.loading && !item.error && item.level && (
                        <Chip 
                          label={item.level}
                          size="small"
                          color={
                            item.level === 'LOW' ? 'success' :
                            item.level === 'MEDIUM' ? 'warning' :
                            item.level === 'HIGH' ? 'error' : 'default'
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {item.loading ? (
                        <Chip label="Loading..." size="small" variant="outlined" />
                      ) : item.error ? (
                        <Chip label="Error" size="small" color="error" variant="outlined" />
                      ) : item.score !== undefined ? (
                        <Chip label="Ready" size="small" color="success" variant="outlined" />
                      ) : (
                        <Chip label="N/A" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filteredData.length === 0 && pagesOCIData.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No pages match the current filters. Try adjusting your search or OCI range.
          </Alert>
        )}

        {filteredData.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Showing {filteredData.length} of {pagesOCIData.length} pages. Click any row to view detailed analysis.
          </Typography>
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              OCI Details - Page #{selectedPageForDetail}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {ociDetailQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : ociDetailQuery.isError ? (
            ociDetailQuery.error?.message === 'OCI_COMPUTING' ? (
              <Alert severity="info">
                <strong>OCI Computation In Progress</strong>
                <br />
                The {window}-day OCI score is being calculated for this page. Please check back in a few minutes.
              </Alert>
            ) : (
              <Alert severity="error">Failed to load OCI data: {ociDetailQuery.error?.message || 'Unknown error'}</Alert>
            )
          ) : insufficientData ? (
            <Alert severity="warning">
              Insufficient data to calculate OCI. Need at least {ociData?.minImpressionsRequired} impressions (found {ociData?.actualImpressions})
            </Alert>
          ) : ociData ? (
            <Box>
              {/* OCI Score Badge */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <OCIBadge score={ociData.score} level={ociData.level} size="large" />
              </Box>

              {/* Dynamic Summary */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'info.50', borderColor: 'info.200' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  What's Happening With This Page:
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  {(() => {
                    const sortedFactors = [...(ociData.factors || [])].sort((a, b) => b.contribution - a.contribution);
                    const topFactor = sortedFactors[0];
                    const secondFactor = sortedFactors[1];
                    const insights = [];
                    
                    if (ociData.level === 'LOW') {
                      insights.push('☀️ This page has LOW competition - rankings and traffic are relatively stable and predictable.');
                    } else if (ociData.level === 'MEDIUM') {
                      insights.push('⛅ This page has MODERATE competition - some fluctuation in performance metrics.');
                    } else if (ociData.level === 'HIGH') {
                      insights.push('🌧️ This page has HIGH competition - significant volatility in rankings and traffic.');
                    } else if (ociData.level === 'VERY_HIGH') {
                      insights.push('⛈️ This page has EXTREME competition - very high volatility across all metrics.');
                    }
                    
                    if (topFactor) {
                      if (topFactor.name === 'click_velocity' && topFactor.contribution > 30) {
                        insights.push(`🎢 Your biggest challenge is UNPREDICTABLE TRAFFIC (${topFactor.contribution.toFixed(1)}%) - daily clicks swing wildly making revenue forecasting difficult.`);
                      } else if (topFactor.name === 'impression_growth' && topFactor.contribution > 30) {
                        insights.push(`👀 Your biggest challenge is UNSTABLE VISIBILITY (${topFactor.contribution.toFixed(1)}%) - your impressions fluctuate significantly day-to-day.`);
                      } else if (topFactor.name === 'position_change' && topFactor.contribution > 30) {
                        insights.push(`📊 Your biggest challenge is RANKING INSTABILITY (${topFactor.contribution.toFixed(1)}%) - your position bounces between different spots frequently.`);
                      } else if (topFactor.name === 'ctr_trend' && topFactor.contribution > 30) {
                        insights.push(`📈 Your biggest challenge is CHANGING USER BEHAVIOR (${topFactor.contribution.toFixed(1)}%) - your click-through rate is trending up or down.`);
                      }
                    }
                    
                    if (secondFactor && secondFactor.contribution > 25) {
                      if (secondFactor.name === 'click_velocity') {
                        insights.push(`Additionally, your daily traffic varies significantly (${secondFactor.contribution.toFixed(1)}%).`);
                      } else if (secondFactor.name === 'impression_growth') {
                        insights.push(`Additionally, your search visibility fluctuates often (${secondFactor.contribution.toFixed(1)}%).`);
                      } else if (secondFactor.name === 'position_change') {
                        insights.push(`Additionally, your rankings are not holding steady (${secondFactor.contribution.toFixed(1)}%).`);
                      }
                    }
                    
                    if (ociData.level === 'LOW') {
                      insights.push('✅ ACTION: This is a good opportunity for quick wins. Small improvements should yield measurable results within weeks.');
                    } else if (ociData.level === 'MEDIUM') {
                      insights.push('⚠️ ACTION: Focus on quality content and technical optimization. Expect 2-3 months to see meaningful improvements.');
                    } else if (ociData.level === 'HIGH') {
                      insights.push('⚠️ ACTION: This requires substantial effort. Plan for 4-6 months of consistent optimization, strong backlinks, and expert content.');
                    } else if (ociData.level === 'VERY_HIGH') {
                      insights.push('💡 ACTION: Consider alternative strategies - target long-tail keywords, create differentiated content angles, or focus on other pages with better ROI potential.');
                    }
                    
                    return insights.map((insight, i) => (
                      <Box key={i} sx={{ mb: i < insights.length - 1 ? 1 : 0 }}>
                        {insight}
                      </Box>
                    ));
                  })()}
                </Typography>
              </Paper>

              {/* OCI Factors */}
              <OCIFactorsList factors={ociData.factors} />

              {/* Metadata */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Calculated:</strong> {new Date(ociData.computedAt).toLocaleString()}<br />
                  <strong>Data Period:</strong> {window} days<br />
                  <strong>Engine:</strong> {engine.toUpperCase()}<br />
                  <strong>Impressions:</strong> {ociData.actualImpressions?.toLocaleString() || 'N/A'}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={recomputeMutation.isPending ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => recomputeMutation.mutate()}
            disabled={recomputeMutation.isPending || !selectedPageForDetail}
          >
            Recompute
          </Button>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
